/**
 * Customer Authentication Context
 * Manages QR-specific customer authentication state
 * SEPARATE from existing AuthContext (which is for business users)
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
  useMemo,
} from "react";
import { toast } from "react-toastify";
import {
  CustomerAuthContextType,
  CustomerProfileDto,
  RegisterRequest,
  UpdateProfileRequest,
  CUSTOMER_AUTH_STORAGE_KEYS,
} from "../types/customer-auth.types";
import {
  loginCustomer as apiLogin,
  registerCustomer as apiRegister,
  getCustomerProfile as apiGetProfile,
  updateCustomerProfile as apiUpdate,
} from "../api/customer-auth.api";
import {
  storeAuthData,
  retrieveAuthData,
  clearAuthData,
  clearCart,
  isTokenValid,
  safeLocalStorage,
} from "../services/customerAuthService";

const CustomerAuthContext = createContext<
  CustomerAuthContextType | undefined
>(undefined);

interface CustomerAuthProviderProps {
  children: ReactNode;
}

/**
 * Customer Auth Context Provider
 * Wraps the entire app to provide auth state
 */
export const CustomerAuthProvider: React.FC<CustomerAuthProviderProps> = ({
  children,
}) => {
  // State
  const [customerAuthId, setCustomerAuthId] = useState<string | null>(null);
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const [restaurantId, setRestaurantId] = useState<number | null>(null);
  const [customerToken, setCustomerToken] = useState<string | null>(null);
  const [customerData, setCustomerData] = useState<CustomerProfileDto | null>(
    null
  );
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Initialize auth on app mount
   * Validates existing token if present
   */
  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = useCallback(async () => {
    setIsLoading(true);

    try {
      const storedData = retrieveAuthData();

      if (!storedData.token) {
        setIsInitialized(true);
        setIsLoading(false);
        return;
      }

      // Check if current QR token matches stored QR token
      // Extract QR token from current URL path (e.g., /qr/TOKEN123) or from sessionStorage
      const urlMatch = window.location.pathname.match(/\/qr\/([^/]+)/);
      const currentQrToken = urlMatch ? urlMatch[1] : sessionStorage.getItem('qrToken');
      
      // Only clear auth if we have both tokens and they don't match
      // If currentQrToken is null (user on non-QR page), we allow auth to persist
      if (currentQrToken && storedData.qrToken && currentQrToken !== storedData.qrToken) {
        // Different QR code - clear auth
        clearAuthData();
        clearCart();
        setIsInitialized(true);
        setIsLoading(false);
        return;
      }

      // Validate token
      if (!isTokenValid(storedData.token)) {
        clearAuthData();
        clearCart();
        setIsInitialized(true);
        setIsLoading(false);
        return;
      }

      // Token is valid, restore state
      setCustomerToken(storedData.token);
      setCustomerAuthId(storedData.customerId);
      setPhoneNumber(storedData.phoneNumber);
      setRestaurantId(storedData.restaurantId);
      setIsAuthenticated(true);

      // Try to fetch fresh profile data
      try {
        const profile = await apiGetProfile(storedData.token);
        setCustomerData(profile);
      } catch (profileError: any) {
        // Profile fetch failed, but token is valid - keep user logged in
      }
    } catch (err: any) {
      clearAuthData();
      clearCart();
    } finally {
      setIsInitialized(true);
      setIsLoading(false);
    }
  }, []);

  /**
   * Login customer
   */
  const loginCustomer = useCallback(
    async (
      phoneNum: string,
      password: string,
      restaurantIdParam: number
    ) => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await apiLogin({
          restaurantId: restaurantIdParam,
          phoneNumber: phoneNum,
          password,
        });

        // Check if we have a valid response with token and data
        if (response && response.token) {
          // Get current QR token from session
          const currentQrToken = sessionStorage.getItem('qrToken');
          
          // Store data first (will store with or without customerData)
          storeAuthData(response.token, response.customerData || {
            id: response.customerAuthId,
            fullName: "",
            phoneNumber: response.phoneNumber,
            email: "",
            restaurantId: response.restaurantId,
            isActive: true,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          } as any, currentQrToken || undefined);

          // Update all state variables immediately
          setCustomerToken(response.token);
          setCustomerAuthId(response.customerAuthId);
          setPhoneNumber(response.phoneNumber);
          setRestaurantId(response.restaurantId);
          
          // Set customer data if available, otherwise use minimal data
          if (response.customerData) {
            setCustomerData(response.customerData);
          } else {
            // Create minimal customer data from response
            setCustomerData({
              id: response.customerAuthId,
              fullName: "",
              phoneNumber: response.phoneNumber,
              email: "",
              restaurantId: response.restaurantId,
              isActive: true,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              currentPointBalance: 0,
              totalPointsEarned: 0,
              totalSpent: 0,
              visitCount: 0,
              expiryPoints: 0,
            });
          }
          
          setIsAuthenticated(true);

          // Fetch full profile data immediately after login
          try {
            const profile = await apiGetProfile(response.token);
            setCustomerData(profile);
          } catch (profileError) {
            // Use minimal data if profile fetch fails
          }
        } else {
          // No token in response - this is an error case
          const errorMsg = response?.message || "Login failed - no token received";
          setError(errorMsg);
          throw new Error(errorMsg);
        }
      } catch (err: any) {
        const errorMessage =
          err.message || "Login failed. Please try again.";
        setError(errorMessage);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  /**
   * Register customer (and auto-login)
   */
  const registerCustomer = useCallback(
    async (registerData: RegisterRequest) => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await apiRegister(registerData);

        // Check if we have a valid response with token and data
        if (response && response.token) {
          // Store data first (will store with or without customerData)
          storeAuthData(response.token, response.customerData || {
            id: response.customerAuthId,
            fullName: registerData.fullName,
            phoneNumber: response.phoneNumber,
            email: registerData.email,
            restaurantId: response.restaurantId,
            isActive: true,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          } as any);

          setCustomerToken(response.token);
          setCustomerAuthId(response.customerAuthId);
          setPhoneNumber(response.phoneNumber);
          setRestaurantId(response.restaurantId);
          
          // Set customer data if available, otherwise use registration data
          if (response.customerData) {
            setCustomerData(response.customerData);
          } else {
            setCustomerData({
              id: response.customerAuthId,
              fullName: registerData.fullName,
              phoneNumber: response.phoneNumber,
              email: registerData.email,
              restaurantId: response.restaurantId,
              isActive: true,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              currentPointBalance: 0,
              totalPointsEarned: 0,
              totalSpent: 0,
              visitCount: 0,
              expiryPoints: 0,
            });
          }
          
          setIsAuthenticated(true);

          // Fetch full profile data immediately after registration
          try {
            const profile = await apiGetProfile(response.token);
            setCustomerData(profile);
          } catch (profileError) {
            console.error("Failed to fetch profile after registration, using registration data");
          }
        } else {
          // No token in response - this is an error case
          const errorMsg = response?.message || "Registration failed - no token received";
          setError(errorMsg);
          throw new Error(errorMsg);
        }
      } catch (err: any) {
        const errorMessage =
          err.message || "Registration failed. Please try again.";
        setError(errorMessage);
        console.error("Registration error:", err);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  /**
   * Logout customer
   */
  const logoutCustomer = useCallback(() => {
    clearAuthData();
    clearCart();

    setCustomerToken(null);
    setCustomerAuthId(null);
    setPhoneNumber(null);
    setRestaurantId(null);
    setCustomerData(null);
    setIsAuthenticated(false);
    setError(null);
  }, []);

  /**
   * Update customer profile
   */
  const updateProfile = useCallback(
    async (updateData: UpdateProfileRequest) => {
      if (!customerToken) {
        throw new Error("Not authenticated");
      }

      setIsLoading(true);
      setError(null);

      try {
        const updatedProfile = await apiUpdate(customerToken, updateData);
        setCustomerData(updatedProfile);

        if (updatedProfile.fullName) {
          safeLocalStorage.setItem(CUSTOMER_AUTH_STORAGE_KEYS.FULL_NAME, updatedProfile.fullName);
        }
        if (updatedProfile.email) {
          safeLocalStorage.setItem(CUSTOMER_AUTH_STORAGE_KEYS.EMAIL, updatedProfile.email);
        }
        if (updatedProfile.profilePictureUrl) {
          safeLocalStorage.setItem(CUSTOMER_AUTH_STORAGE_KEYS.PROFILE_PICTURE, updatedProfile.profilePictureUrl);
        } else {
          safeLocalStorage.removeItem(CUSTOMER_AUTH_STORAGE_KEYS.PROFILE_PICTURE);
        }
        
        toast.success("Profile updated successfully", {
          toastId: 'profile-update-success',
          position: "top-center",
          autoClose: 2000,
        });
      } catch (err: any) {
        const errorMessage =
          err.message || "Failed to update profile. Please try again.";
        setError(errorMessage);
        toast.error(errorMessage, {
          toastId: 'profile-update-error',
        });
        console.error("Update profile error:", err);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [customerToken]
  );

  /**
   * Refresh profile data from server
   */
  const refreshProfile = useCallback(async () => {
    if (!customerToken) {
      throw new Error("Not authenticated");
    }

    setIsLoading(true);
    setError(null);

    try {
      const profile = await apiGetProfile(customerToken);
      setCustomerData(profile);
    } catch (err: any) {
      const errorMessage =
        err.message || "Failed to refresh profile. Please try again.";
      setError(errorMessage);
      console.error("Refresh profile error:", err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [customerToken]);

  /**
   * Clear error message
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Memoize the context value to prevent unnecessary re-renders of consumers
  const value: CustomerAuthContextType = useMemo(
    () => ({
      customerAuthId,
      phoneNumber,
      restaurantId,
      customerToken,
      customerData,
      isAuthenticated,
      isLoading,
      isInitialized,
      error,
      loginCustomer,
      registerCustomer,
      logoutCustomer,
      updateProfile,
      refreshProfile,
      clearError,
    }),
    [
      customerAuthId,
      phoneNumber,
      restaurantId,
      customerToken,
      customerData,
      isAuthenticated,
      isLoading,
      isInitialized,
      error,
      loginCustomer,
      registerCustomer,
      logoutCustomer,
      updateProfile,
      refreshProfile,
      clearError,
    ]
  );

  return (
    <CustomerAuthContext.Provider value={value}>
      {children}
    </CustomerAuthContext.Provider>
  );
};

/**
 * Hook to use Customer Auth Context
 */
export const useCustomerAuthContext = (): CustomerAuthContextType => {
  const context = useContext(CustomerAuthContext);

  if (context === undefined) {
    throw new Error(
      "useCustomerAuthContext must be used within CustomerAuthProvider"
    );
  }

  return context;
};
